module Client.AdminEditFiles

open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Style
open Fable.Core
open Fable.Core.JsInterop
open Fetch
open Fable
open Shared
open Utils
open AdminShared
open System
open Fable.Import
open Browser.Types
open Browser.Dom
open Fable.SimpleHttp

type Msg =
    | FetchError of exn                                                                                                                                          
    | ShowUploadFileDialog
    | HideUploadFileDialog
    | FileClicked of id: Domain.FileMeta * ClickAction
    | FileSelected of Filename: string
    | UploadFile of Domain.FileMeta * Browser.Types.Blob
    | LoadedFileList of Domain.FileMeta list
    | ConfirmedFileDeletion
    | CancelDeletion
    | FileDeleted
    | FileUploaded
    | FileUploadResponse of Validated
    | FileUploadError of string
    | ReturnToFileList

type FileState =
    {
        File: Domain.FileMeta
        IsNew: bool
    }

type AdminFileAction =
    | Listing of LoadState<unit, FileMeta list>
    | Editing of LoadState<FileMeta, FileState>

type Model = 
    { 
        User: JWT;
        Action: AdminFileAction;
        ShowConfirmDelete: bool;
        ShowAddNew: bool
        Filename: string option
        Processing: bool
        FileBeingDeleted: FileMeta option
        ErrorMessages: string list
    }

[<Fable.Core.Emit("$0.files[0]")>]
let private getFile (ele:HTMLElement) : Blob = jsNative

let postUploadFile (tenant: string, token, file: Blob): JS.Promise<Validated> =
    promise {
        let url = Urls.Files tenant
        let formData = FormData.create()
        formData.append("file", file)
        let props = 
            [
                RequestProperties.Method Fetch.Types.HttpMethod.POST
                RequestProperties.Credentials RequestCredentials.Sameorigin
                RequestProperties.Body !!formData
                Fetch.requestHeaders [
                    HttpRequestHeaders.Authorization ("Bearer " + token)
                ]
            ]
        return! Thoth.Fetch.Fetch.post(url, properties=props)
    }

let getAllFilesCmd (jwt: JWT) =
    let url = Urls.Files (TenantRef.get jwt.Account.Tenant)
    FetchRequest.getWithToken url jwt.Token LoadedFileList FetchError

let uploadFileCmd (jwt: JWT) file = 
    Cmd.OfPromise.either postUploadFile (TenantRef.get jwt.Account.Tenant, jwt.Token, file) FileUploadResponse FetchError

let deleteFileCmd (jwt: JWT) (filename: string) = 
    let url = Urls.Files (TenantRef.get jwt.Account.Tenant)
    FetchRequest.deleteWithToken url filename jwt.Token (fun _ -> FileDeleted) FetchError

let init user = 
    {
        User = user
        ShowConfirmDelete = false
        ShowAddNew = false
        Action = Listing (Loading ())
        Filename = None
        Processing = false
        FileBeingDeleted = None
        ErrorMessages = []
    }, getAllFilesCmd user

let private editing model modifier cmd success =
     match model.Action with
        | Editing (Loaded pageState) ->  
           modifier model pageState, (cmd model pageState), (success pageState)
        | _ -> failwith "Invalid action"

let private editingAction model modifier cmd success =
     editing model (fun m pageState -> { m with Action = Editing (Loaded(modifier pageState)) }) cmd success

let private editingActionU model modifier cmd = editingAction model modifier cmd (fun _ -> Unchanged)

let update (msg:Msg) model : Model*Cmd<Msg>* SuccessMessageChange =    
    match msg with
    | UploadFile(meta, blob) -> 
        if blob.size >= 10_000_000 then
            { model with ErrorMessages = ["The maximum file size is 10 mb."] }, [], Unchanged
        else
            model, uploadFileCmd model.User blob, Unchanged
    | FileUploadError err ->
        { model with ErrorMessages = [err] }, [], Unchanged
    | FileClicked (file, action) ->
        match action with
        | Edit ->
            { model with ErrorMessages = []; Action = Editing (Loaded { File = file; IsNew = false }) }, [], Unchanged
        | Delete ->
            { model with ErrorMessages = []; ShowConfirmDelete = true; FileBeingDeleted = Some file }, [], Unchanged     
    | LoadedFileList files ->
        { model with Action = Listing (Loaded files) }, [], Unchanged                                                                        
    | CancelDeletion -> 
        { model with ShowConfirmDelete = false; FileBeingDeleted = None; }, [], Unchanged
    | ConfirmedFileDeletion -> 
        let filename = match model.FileBeingDeleted with Some f -> f.Name | None -> failwithf "Invalid state"
        model, deleteFileCmd model.User filename, Unchanged
    | FileDeleted ->
        let filename = match model.FileBeingDeleted with Some m -> m.Name | None -> failwith "Invalid state"
        { model with FileBeingDeleted = None; ShowConfirmDelete = false }, Cmd.ofMsg ReturnToFileList, Show (sprintf "The file '%s' was deleted." filename)
    | FileUploaded ->
        let filename = match model.Filename with Some m -> m | None -> "???"
        { model with ShowAddNew = false; Filename = None }, Cmd.ofMsg ReturnToFileList, Show (sprintf "The file '%s' has been uploaded." filename)
    | ReturnToFileList ->
        { model with Action = Listing (Loading()); ShowConfirmDelete = false },getAllFilesCmd (model.User),Unchanged
    | FetchError exn ->
        { model with ErrorMessages = [ exn.Message ] },[],Unchanged
    | ShowUploadFileDialog -> 
        { model with ShowAddNew = true; Filename = None; ErrorMessages = [] }, [], Unchanged
    | HideUploadFileDialog -> 
       { model with ShowAddNew = false; Filename = None }, [], Unchanged
    | FileSelected name ->
        { model with Filename = Some name; }, [], Unchanged
    | FileUploadResponse result ->
        match result with
        | Success -> model, Cmd.ofMsg FileUploaded, Unchanged
        | Failure msgs -> { model with ErrorMessages = msgs }, [], Unchanged


let private spinner() = i [ ClassName "glyphicon glyphicon-cog" ] []

let view model (dispatch: Msg -> unit) =
    match model.Action with
    | Listing loadstate ->
        match loadstate with
        | Loading () -> 
            [
                yield div [ ClassName "valign" ] [
                    yield h2 [ Style [ MarginRight "10px" ] ] [ str "Uploaded Files" ] 
                    yield button [ 
                        ClassName "btn animated-background" 
                        HTMLAttr.Type "button"
                    ] [ str "Upload a file" ]
                ]
                yield div [] [ str  "There are ? uploaded files" ]
                yield table [ ClassName "table animated-background" ] [
                    thead [] [
                        tr [ ClassName "mask" ] [
                            th [ClassName "mask"] []
                            th [ClassName "mask"] [ str "Filename" ]
                            th [ClassName "mask"] [ str "Size" ]
                            th [ClassName "mask"] [ str "Uploaded" ]
                            th [ClassName "mask"] []
                        ]
                    ]
                    tbody [] [
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [ str " "]
                            td [ Style [ TextAlign TextAlignOptions.Right ] ] [
                                a [ jsVoid ] [ str "Go to" ]
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                                
                        ]
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [ str " "]
                            td [] [ str " "]
                            td [ Style [ TextAlign TextAlignOptions.Right ] ] [
                                a [ jsVoid ] [ str "Go to" ]
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                              
                        ]
                        tr [] [ 
                            td [] [ str " "]
                            td [] [ str " "]
                            td [ Style [ TextAlign TextAlignOptions.Right ] ] [
                                a [ jsVoid ] [ str "Go to" ]                                                                                      
                                a [ jsVoid ] [ str "Edit" ]
                                a [ jsVoid ] [ str "Delete" ]
                            ]                                                                    
                        ]
                    ]
                ]
            ]
        | Loaded files ->
            [
                yield div [ ClassName "valign" ] [
                    yield h2 [ Style [ MarginRight "10px" ] ] [ str "Uploaded Files" ] 
                    yield button [ 
                        ClassName "btn btn-default" 
                        HTMLAttr.Type "button"
                        OnClick (fun _ -> dispatch ShowUploadFileDialog)
                    ] [ str "Upload a file" ]
                ]
                match files.Length with
                | 0 -> 
                    yield p [ ClassName "bg-info" ] [ str "You have not yet uploaded any files. Click the 'Upload a file' button to add your first." ]
                | n ->
                    yield div [] [ str <| sprintf "There %s %d uploaded fil%s." (be n) n (plural "e" n) ]
                    yield table [ ClassName "table thumbs" ] [
                        thead [] [
                            tr [] [
                                th [] []
                                th [] [ str "Filename" ]
                                th [] [ str "Size" ]
                                th [] [ str "Uploaded" ]
                                th [] []
                            ]
                        ]
                        tbody [] [
                            yield! files |> List.map (fun file -> 
                                tr [] [ 
                                    td [] [ 
                                        if file.Thumbnail = "" then
                                            ()
                                        else
                                            yield img [ 
                                                HTMLAttr.Height "32px"
                                                HTMLAttr.Width "32px"
                                                Alt "thumbnail"
                                                Src (sprintf "data:image/png;base64,%s" file.Thumbnail)
                                            ]                                            
                                    ]
                                    td [] [ str file.Name ]
                                    td [] [ str <| sprintf "%d KB" (file.Size / 1000) ]
                                    td [] [ str <| file.DateUploaded.ToString("dd/MMM/yyyy") ]
                                    td [] [ 
                                        a [ jsVoid; OnClick (fun _ ->  dispatch (FileClicked(file, Delete))) ] [ str "Delete" ]
                                    ]                                                                    
                                ])
                        ]
                    ]

                if model.ShowAddNew then
                    yield! modal "Upload a file" dispatch HideUploadFileDialog [
                        yield div [ ClassName "centerflex" ] [
                            button [
                                Style [
                                    Width "150px"
                                ]
                                OnClick (fun _ -> 
                                    let upload = document.getElementById "csvUpload"
                                    upload.click()
                                    )
                                ClassName "btn btn-info"
                                HTMLAttr.Type "button"
                            ] [ str "Browse" ]
                            input [ 
                                HTMLAttr.Type "text" 
                                HTMLAttr.Value (match model.Filename with None -> "No file selected" | Some name -> name)
                                Disabled true
                                Style [
                                    Width "100%"
                                    MarginLeft "10px"
                                    MarginRight "10px"
                                    Height "34px"
                                    LineHeight "34px"
                                ]
                            ]
                            input [ 
                                Id "csvUpload"
                                HTMLAttr.Type "file"
                                Placeholder "Import a csv file"
                                OnChange (fun ev -> 
                                    let file = getFile !!ev.target
                                    dispatch (FileSelected !!file?name)
                                )
                                Style [
                                    Display DisplayOptions.None
                                ]
                            ]
                            button [
                                Style [
                                    Width "150px"
                                ]
                                OnClick (fun _ ->
                                    let upload = document.getElementById "csvUpload"
                                    let file = getFile upload
                                    dispatch (UploadFile ({ Name = !!file?name; Size = !!file?size; DateUploaded = System.DateTime.UtcNow; Thumbnail = ""; Image = false }, file)))
                                Disabled (model.Filename.IsNone)
                                ClassName "btn btn-default"
                                HTMLAttr.Type "button"

                            ] [ 
                                if model.Processing then yield spinner()
                                else yield str "Upload file";  
                            ] 
                        ]
                        if model.ErrorMessages.Length > 0 then
                            yield ul [ ClassName "alert alert-danger" ] (model.ErrorMessages |> List.map (fun x -> li [] [ str x ]))
                    ] []
                match model.ShowConfirmDelete with
                | false -> ()
                | true -> 
                    yield! modal "Confirm Deletion" dispatch CancelDeletion [
                        yield p [] [ str "Are you sure you wish to delete this file upload? No pages will be modified - anything that links to this file will now have a broken link." ]

                        if model.ErrorMessages.Length > 0 then
                            yield ul [ ClassName "alert alert-danger" ] (model.ErrorMessages |> List.map (fun x -> li [] [ str x ]))

                    ] [
                        button [ ClassName "btn btn-danger";HTMLAttr.Type "button"; OnClick (fun _ -> dispatch ConfirmedFileDeletion) ] [ str "Confirm Deletion" ]
                        button [ ClassName "btn btn-default"; HTMLAttr.Type "button";OnClick (fun _ -> dispatch CancelDeletion); Keyboard.onEscape CancelDeletion dispatch] [ str "Cancel" ]
                    ]
            ]
